import { Button, ClickAwayListener, Pagination, PaginationItem, Tooltip } from "@mui/material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { smetaModals, SmetaModalsIdContext } from "../../Tools/modalManager";
import Params from "../../Sections/ElementsSections/Params/Params";
import { formEdit } from "../../Tools/Tools";
import { DefaultDialogContentProps } from "./DialogAcctools";
import clsx from "clsx";
import dialogStyles from "./Components/dialogStyles.module.css";

const switchFocusObj: { [key: string]: string } = {
  main: "buttons",
  buttons: "main",
};

interface Changes {
  objref?: string;
  value?: string;
  datatype?: string;
  checkstate?: string;
  shortname?: string;
}
interface PostChanges {
  ObjRef?: string;
  Value?: string;
  DataType?: string;
  CheckState?: string;
}

const getPostData = (changes: { [key: string]: Changes }) => {
  return Object.entries(changes).reduce((acc: { [key: string]: PostChanges }, [key, value]) => {
    acc[value.shortname || key] = {
      ObjRef: value.objref,
      Value: value.value,
      DataType: value.datatype,
      CheckState: value.checkstate,
    };
    return acc;
  }, {});
};

/**
 * Контент диалога c таблицей параметров
 *
 * Поля компонента:
 *    1. [pagination, setPagination] - управление состоянием страниц
 *    2. [currentPage, setCurrentPage] - управление состоянием выбора страниц
 *    3. [changes, setChanges] - управление состоянием сделанных изменений
 *    4. paramBox - ссылка на контейнер с таблицей параметров
 *    5. buttonsContainer - ссылка на контейнер с кнопками
 *    6. focusSwitch - контроль выбора фокуса
 *    7. focusButtonIndex - контроль выбора кнопки с фокусом
 *
 * @param {Object} [props] объект с данными для компонента диалога параметров
 * @param {Object} [props.data.Module] имя редактируемого поля
 * @param {string} [props.ObjType] тип объекта
 * @param {Object} [props.data] объект с данными для параметров
 * @param {Object} [props.data.onSelect] функция на редактирование
 * @param {Object} [props.data.reqCommand] данные для запроса параметров
 * @param {Object} [props.data.GroupID] id группы
 * @param {Object} [props.data.Path] путь для параметров
 * @param {Object} [props.data.NoTitle] выключить заголовок
 * @param {Object} [props.data.NoHistory] выключить историю
 * @param {Object} [props.data.ExecEvent] особое редактирование
 * @param {Object} [props.data.RequestID] id цепи запросов
 * @param {Object} [props.data.from] откуда был вызов
 * @param {Object} [props.data.Buttons] управление отображения кнопок
 * @param {Object} [props.data.hiddenFutter] скрыть футтер
 * @param {Object} [props.data.onDropDownList] функиця для выпадающего списка в параметрах
 * @param {Object} [props.data.onEditList] функция на редактирование в параметрах
 * @param {Object} [props.data.onSetParam] функция на установку параметров
 * @param {Object} [props.data.onClose] функция на закрытие параметров
 * @param {Object} [props.data.func] функция выполняющаяся в конце цепи запросов
 *
 * @returns Возвращает контент диалога с таблицей параметров
 */
export default function DialogParams({ props }: DefaultDialogContentProps) {
  const {
    Module: module,
    reqCommand,
    GroupID: groupId,
    Path: path,
    NoTitle: noTitle,
    NoHistory: noHistory,
    data,
    Buttons: buttons,
    ExecEvent: execEvent,
    RequestID: requestId,
    from,
    hiddenFutter,
    onDropDownList,
    onEditList,
    onSetParam,
    onSelect,
    onClose,
    func,
  } = props;

  const [pagination, setPagination] = useState<any[]>();
  const [currentPage, setCurrentPage] = useState<number>();
  const [changes, setChanges] = useState<{ [key: string]: Changes }>({});

  const paramBox = useRef<any>(null);
  const buttonsContainer = useRef<any>(null);
  const focusSwitch = useRef<string>("main");
  const focusButtonIndex = useRef(0);
  const smetaModalId = useContext(SmetaModalsIdContext);

  /**
   * Инициализация пагинации и фокус на контейнер с таблицей параметров
   */
  const initParamBox = () => {
    if (data?.Pages && data.Pages.length) {
      setPagination(data.Pages);
      setCurrentPage(1);
    }
    setTimeout(() => {
      if (paramBox.current) {
        paramBox.current.focus();
        const pBox = paramBox.current.querySelector("#paramsBox");
        if (pBox) pBox.focus();
      }
    });
  };
  useEffect(initParamBox, []);

  /**
   * Выполняет фокус на противоположный контейнер и первую кнопку
   */
  const onKeyEnter = () => {
    if (focusSwitch.current === "main") {
      setTimeout(() => {
        focusSwitch.current = switchFocusObj[focusSwitch.current];
        buttonsContainer.current.children[0].focus();
        focusButtonIndex.current = 0;
      }, 200);
    }
  };

  /**
   * Выполняет смену фокуса между двумя контейнерами
   * @param ev событие нажатия клавиши
   */
  const onKeyTab = (ev: any) => {
    ev.preventDefault();
    const switcher: { [key: string]: () => void } = {
      main: () => {
        focusSwitch.current = switchFocusObj[focusSwitch.current];
        buttonsContainer.current.children[0].focus();
        focusButtonIndex.current = 0;
      },
      buttons: () => {
        focusSwitch.current = switchFocusObj[focusSwitch.current];
        if (paramBox.current) {
          paramBox.current.focus();
          const pBox = paramBox.current.querySelector("#paramsBox");
          if (pBox) pBox.focus();
        }
      },
    };

    switcher[focusSwitch.current]?.();
  };

  /**
   * Фокусирует на кнопку левее
   */
  const onKeyArrowLeft = () => {
    if (focusSwitch.current === "buttons") {
      const bttns = buttonsContainer.current.children;
      if (focusButtonIndex.current > 0) {
        focusButtonIndex.current = focusButtonIndex.current - 1;
        bttns[focusButtonIndex.current].focus();
      }
    }
  };

  /**
   * Фокусирует на кнопку правее
   */
  const onKeyArrowRight = () => {
    if (focusSwitch.current === "buttons") {
      const bttns = buttonsContainer.current.children;
      if (focusButtonIndex.current < bttns.length - 1) {
        focusButtonIndex.current = focusButtonIndex.current + 1;
        bttns[focusButtonIndex.current].focus();
      }
    }
  };

  /**
   * Реализует кастомную фокусировку
   * @param ev событие нажатия клавиш
   */
  function onKeyDown(ev: any) {
    const keyEvents: { [key: string]: (ev?: any) => void } = {
      NumpadEnter: onKeyEnter,
      Enter: onKeyEnter,
      Tab: onKeyTab,
      ArrowLeft: onKeyArrowLeft,
      ArrowRight: onKeyArrowRight,
    };
    keyEvents[ev.code]?.(ev);
  }

  /**
   * Выполняет функцию выбора
   * @returns
   */
  async function onSelectClick() {
    if (execEvent) {
      const pBox = paramBox.current.querySelector("#paramsBox");
      await pBox?.dialogButtonClick("-1", execEvent, async () => formEdit("1", func, {}, requestId, from));
      return;
    }

    onSelect?.(getPostData(changes));

    smetaModals.close(smetaModalId);

    if (requestId) {
      await formEdit("1", func, {}, requestId, from);
      return;
    }
  }

  return (
    <div id={"mainElement"} onKeyDown={onKeyDown} className={clsx(dialogStyles.mainDialogBlock)}>
      <div className={clsx(dialogStyles.fullDialogFlexContainer, dialogStyles.dialogDirectionColumn)}>
        <div className={clsx(dialogStyles.fullDialogFlexContainer)} tabIndex={0} ref={paramBox}>
          <Params
            currentPage={currentPage !== undefined ? currentPage - 1 : undefined}
            pageTitles={pagination}
            Module={module}
            reqCommand={reqCommand}
            GroupID={groupId}
            Path={path}
            data={data}
            NoTitle={noTitle}
            NoHistory={noHistory}
            setChanges={setChanges}
            onDropDownList={onDropDownList}
            onEditList={onEditList}
            onSetParam={onSetParam}
            requestId={requestId}
            from={from}
            func={func}
          />
        </div>
        {currentPage !== undefined && pagination !== undefined && (
          <div className={clsx(dialogStyles.dialogParamsPaginationContainer)}>
            <Pagination
              page={currentPage}
              onChange={(ev, id) => {
                setCurrentPage(Number(id));
              }}
              count={pagination.length}
              renderItem={(item) => {
                if (item.type === "page") {
                  return (
                    <Tooltip title={pagination[Number(item.page) - 1].Name}>
                      <span>
                        <PaginationItem {...item} />
                      </span>
                    </Tooltip>
                  );
                } else {
                  return <PaginationItem {...item} />;
                }
              }}
            />
          </div>
        )}
      </div>

      <div className={clsx(dialogStyles.dialogButtonsContainer, hiddenFutter && dialogStyles.dialogHidden)}>
        <ClickAwayListener
          onClickAway={(ev) => {
            focusSwitch.current = "main";
          }}
        >
          <div>
            <div ref={buttonsContainer}>
              {/* {Кнопка Выбрать} */}
              {currentPage !== undefined && pagination !== undefined && currentPage !== pagination.length ? (
                <Button
                  tabIndex={0}
                  onClick={() => {
                    setCurrentPage(currentPage + 1);
                  }}
                  size="small"
                  variant="outlined"
                  style={{
                    textTransform: "none",
                    marginRight: "10px",
                  }}
                  // onKeyDown={onKeyDown}
                >
                  Дальше
                </Button>
              ) : (
                <>
                  {currentPage !== undefined && (
                    <Button
                      tabIndex={0}
                      onClick={() => {
                        setCurrentPage(currentPage - 1);
                      }}
                      size="small"
                      variant="outlined"
                      style={{
                        textTransform: "none",
                        marginRight: "10px",
                      }}
                    >
                      Назад
                    </Button>
                  )}
                  <Button
                    tabIndex={0}
                    onClick={onSelectClick}
                    size="small"
                    variant="outlined"
                    style={{
                      textTransform: "none",
                      marginRight: "10px",
                      display: Number(buttons) && (Number(buttons) & 1) === 0 ? "none" : "",
                    }}
                    value="1"
                    className="button_Modal_Select"
                  >
                    {execEvent ? "Выполнить" : "Выбрать"}
                  </Button>
                </>
              )}

              {/* {кнопка Закрыть} */}
              <Button
                tabIndex={0}
                size="small"
                variant="outlined"
                style={{ textTransform: "none" }}
                value="2"
                className="button_Modal_Close"
                onClick={() => {
                  onClose?.();
                  smetaModals.close(smetaModalId);
                }}
              >
                Закрыть
              </Button>
            </div>
          </div>
        </ClickAwayListener>
      </div>
    </div>
  );
}
